(() => {
  const SECTION_SELECTOR = '[data-mbb-story]';
  const REVEAL_SELECTOR = '[data-mbb-story-reveal]';
  const IMAGE_SELECTOR = '.mbb-story__image';

  class MbbStorySection {
    constructor(section) {
      if (!section || section.dataset.mbbStoryInitialized === 'true') return;

      this.section = section;
      this.image = section.querySelector(IMAGE_SELECTOR);
      this.revealItems = [...section.querySelectorAll(REVEAL_SELECTOR)];

      this.reducedMotion = window.matchMedia(
        '(prefers-reduced-motion: reduce)'
      ).matches;

      this.isVisible = false;
      this.animationFrame = null;
      this.observer = null;

      this.handleScroll = this.handleScroll.bind(this);
      this.handleResize = this.handleResize.bind(this);

      this.init();
    }

    init() {
      this.section.dataset.mbbStoryInitialized = 'true';

      if (
        this.reducedMotion ||
        !('IntersectionObserver' in window)
      ) {
        this.reveal();
        return;
      }

      this.observer = new IntersectionObserver(
        entries => {
          entries.forEach(entry => {
            this.isVisible = entry.isIntersecting;

            if (!entry.isIntersecting) return;

            this.reveal();
            this.updateParallax();
          });
        },
        {
          threshold: 0.12,
          rootMargin: '0px 0px -8% 0px'
        }
      );

      this.observer.observe(this.section);

      window.addEventListener('scroll', this.handleScroll, {
        passive: true
      });

      window.addEventListener('resize', this.handleResize, {
        passive: true
      });
    }

    reveal() {
      this.section.classList.add('is-visible');

      this.revealItems.forEach((item, index) => {
        item.style.transitionDelay = `${index * 110}ms`;
      });
    }

    updateParallax() {
      if (
        !this.image ||
        !this.isVisible ||
        this.reducedMotion ||
        window.innerWidth <= 990
      ) {
        return;
      }

      const rect = this.section.getBoundingClientRect();
      const viewportHeight = window.innerHeight;

      const sectionCenter = rect.top + rect.height / 2;
      const viewportCenter = viewportHeight / 2;

      const progress =
        (viewportCenter - sectionCenter) / viewportHeight;

      const offset = Math.max(
        -18,
        Math.min(18, progress * 30)
      );

      this.image.style.setProperty(
        '--story-parallax',
        `${offset}px`
      );
    }

    resetParallax() {
      if (!this.image) return;

      this.image.style.setProperty(
        '--story-parallax',
        '0px'
      );
    }

    handleScroll() {
      if (
        !this.isVisible ||
        this.reducedMotion ||
        this.animationFrame
      ) {
        return;
      }

      this.animationFrame = requestAnimationFrame(() => {
        this.updateParallax();
        this.animationFrame = null;
      });
    }

    handleResize() {
      if (this.animationFrame) {
        cancelAnimationFrame(this.animationFrame);
      }

      this.animationFrame = requestAnimationFrame(() => {
        if (window.innerWidth <= 990) {
          this.resetParallax();
        } else {
          this.updateParallax();
        }

        this.animationFrame = null;
      });
    }

    destroy() {
      this.observer?.disconnect();

      window.removeEventListener(
        'scroll',
        this.handleScroll
      );

      window.removeEventListener(
        'resize',
        this.handleResize
      );

      if (this.animationFrame) {
        cancelAnimationFrame(this.animationFrame);
      }

      this.section.classList.remove('is-visible');

      this.revealItems.forEach(item => {
        item.style.removeProperty('transition-delay');
      });

      this.image?.style.removeProperty(
        '--story-parallax'
      );

      delete this.section.dataset.mbbStoryInitialized;
      delete this.section.mbbStoryInstance;
    }
  }

  const getSections = root => {
    if (!root) return [];

    if (root.matches?.(SECTION_SELECTOR)) {
      return [root];
    }

    return [
      ...(root.querySelectorAll?.(SECTION_SELECTOR) || [])
    ];
  };

  const initSections = root => {
    getSections(root).forEach(section => {
      if (
        section.dataset.mbbStoryInitialized === 'true'
      ) {
        return;
      }

      section.mbbStoryInstance =
        new MbbStorySection(section);
    });
  };

  const destroySections = root => {
    getSections(root).forEach(section => {
      section.mbbStoryInstance?.destroy();
    });
  };

  const start = () => {
    initSections(document);
  };

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      start,
      { once: true }
    );
  } else {
    start();
  }

  document.addEventListener(
    'shopify:section:load',
    event => {
      initSections(event.target);
    }
  );

  document.addEventListener(
    'shopify:section:unload',
    event => {
      destroySections(event.target);
    }
  );

  document.addEventListener(
    'shopify:section:select',
    event => {
      const sections = getSections(event.target);

      sections.forEach(section => {
        section.classList.add('is-visible');
        section.mbbStoryInstance?.updateParallax();
      });
    }
  );

  document.addEventListener(
    'shopify:section:deselect',
    event => {
      const sections = getSections(event.target);

      sections.forEach(section => {
        section.mbbStoryInstance?.resetParallax();
      });
    }
  );
})();